import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote } from "lucide-react";
import { REVIEWS } from "../../data/content";
import { Reveal } from "./Reveal";

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export const Reviews = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const autoplayRef = useRef(null);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  }, []);

  const startAutoplay = useCallback(() => {
    if (!emblaApi) return;
    stopAutoplay();
    autoplayRef.current = setInterval(() => emblaApi.scrollNext(), 4500);
  }, [emblaApi, stopAutoplay]);

  const scrollTo = useCallback(
    (i) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(i);
      startAutoplay();
    },
    [emblaApi, startAutoplay]
  );

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", stopAutoplay);
    emblaApi.on("pointerUp", startAutoplay);
    startAutoplay();
    return () => {
      stopAutoplay();
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", stopAutoplay);
      emblaApi.off("pointerUp", startAutoplay);
    };
  }, [emblaApi, startAutoplay, stopAutoplay]);

  return (
    <section data-testid="reviews" className="bg-[#f6f1e9]">
      <div className="max-w-6xl mx-auto px-6 pt-20 md:pt-8 pb-20 md:pb-28">
        <Reveal className="flex flex-col items-center text-center mb-12 md:mb-16">
          <div className="flex items-center gap-2">
            <GoogleG />
            <span className="text-sm text-[#4b2018]">Avis Google</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="font-serif-display text-4xl text-[#4b2018]">
              {REVIEWS.rating}
            </span>
            <span className="flex text-[#e8b04b]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} fill="currentColor" strokeWidth={0} />
              ))}
            </span>
          </div>
          <p className="mt-2 text-sm text-[#4b2018]">
            {REVIEWS.count} avis · Ce que disent nos clientes
          </p>
        </Reveal>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-4 md:-ml-6 cursor-grab active:cursor-grabbing">
            {REVIEWS.items.map((r, i) => (
              <div
                key={i}
                data-testid={`review-${i}`}
                className="pl-4 md:pl-6 flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
              >
                <div className="h-full bg-white border border-[#ece4d7] rounded-md p-7 flex flex-col">
                  <Quote size={22} className="text-[#b07a63] mb-4" />
                  <p className="text-[#4f4a42] leading-relaxed flex-1">
                    « {r.text} »
                  </p>
                  <div className="mt-6 pt-5 border-t border-[#ece4d7] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="grid place-items-center h-9 w-9 rounded-full bg-[#f6f1e9] text-[#b07a63] font-serif-display text-lg">
                        {r.author.charAt(0)}
                      </span>
                      <span className="text-sm font-medium text-[#4b2018]">
                        {r.author}
                      </span>
                    </div>
                    <span className="flex text-[#e8b04b]">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} size={13} fill="currentColor" strokeWidth={0} />
                      ))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex justify-center gap-2">
          {snaps.map((_, i) => (
            <button
              key={i}
              aria-label={`Avis ${i + 1}`}
              data-testid={`review-dot-${i}`}
              onClick={() => scrollTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === selected ? "w-6 bg-[#b07a63]" : "w-2 bg-[#d8cdbb]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
